import { Config } from '../../video-data/config';
import type { Asset } from '../asset/BaseAsset';
import type { Scene } from '../scene/Scene';
import type { CircleMask } from '../mask/CircleMask';
import type { PolygonMask } from '../mask/PolygonMask';
import type { StencilMask } from '../mask/StencilMask';
import { holdConfigCurveAtRest } from '../../libs/AnimationCurveManager';
import type { AnimationConfig } from '../../libs/AnimationCurveManager';

export type MorphableProperty =
    | 'x'
    | 'y'
    | 'scale.x'
    | 'scale.y'
    | 'alpha'
    | 'mask.transition'
    | 'scaleX'
    | 'scaleY'
    | 'rotation'
    | 'transform.position.x'
    | 'transform.position.y'
    | 'xTranslation'
    | 'yTranslation'
    | 'rectWidth'
    | 'rectHeight';

export type MorphLinkedPropertySpecs<T> = Partial<
    Record<
        keyof T & string,
        {
            isEqual?: (a: T, b: T) => boolean;
            sync?: (target: T, source: T) => void;
        }
    >
>;

type MorphSourceState = {
    asset: Asset;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    alpha: number;
    rotation: number;
};

export class MorphManager {
    #slide: Scene;

    static #mediaTimeOffsets: Map<string, number> = new Map();
    static #allTargetAssets: Map<string, Asset> = new Map();
    static #morphPairs: Map<string, string> = new Map();
    static #reverseMorphPairs: Map<string, string> = new Map();
    static #isUpdating = false;

    static getMediaTimeOffset(assetId: string): number | null {
        return MorphManager.#mediaTimeOffsets.get(assetId) ?? null;
    }

    static isMorphTarget(assetId: string): boolean {
        return MorphManager.#allTargetAssets.has(assetId);
    }

    #sourceStates: Map<string, MorphSourceState> = new Map();
    #targetAssets: Set<Asset> = new Set();

    constructor(slide: Scene) {
        this.#slide = slide;
    }

    setupMorphAnimations() {
        if (MorphManager.#isUpdating) {
            return;
        }

        const nextScene = this.#slide.nextScene;
        if (!nextScene) {
            return;
        }

        MorphManager.#isUpdating = true;
        try {
            const sourceAssets = this.#slide
                .getAssets({ deep: true })
                .sort((a, b) => a.name.localeCompare(b.name));
            const targetAssets = nextScene
                .getAssets({ deep: true })
                .sort((a, b) => a.name.localeCompare(b.name));

            sourceAssets.forEach((sourceAsset) => {
                const matchingAsset = targetAssets.find(
                    (targetAsset) =>
                        !this.#targetAssets.has(targetAsset) &&
                        sourceAsset.isMorphMatch(targetAsset),
                );
                if (!matchingAsset) {
                    return;
                }

                this.#setupMorphForAssetPair(sourceAsset, matchingAsset);
            });

            for (const { asset } of this.#sourceStates.values()) {
                if (!asset.hasMorphAnimation) {
                    continue;
                }
                MorphManager.#forceBuildAndDraw(asset);
            }
        } finally {
            MorphManager.#isUpdating = false;
        }
    }

    clearMorphAnimations() {
        if (MorphManager.#isUpdating) {
            return;
        }

        MorphManager.#isUpdating = true;
        try {
            for (const state of this.#sourceStates.values()) {
                const { asset, x, y, scaleX, scaleY, alpha, rotation } = state;

                if (!asset || asset.destroyed) {
                    continue;
                }

                asset.clearMorphAnimations();

                asset.transformation.position.x = x;
                asset.transformation.position.y = y;
                asset.scale.x = scaleX;
                asset.scale.y = scaleY;
                asset.alpha = alpha;
                asset.transformation.rotation = rotation;

                if (asset.mask && Config.isStencilMask(asset.mask)) {
                    (asset.mask as CircleMask | PolygonMask)._clearMorphState();
                }

                MorphManager.#forceBuildAndDraw(asset);
            }
            MorphManager.#clearPairMaps(this.#sourceStates);
            MorphManager.#clearTargetAssets(this.#targetAssets);
        } finally {
            MorphManager.#isUpdating = false;
        }
    }

    resetMorphAnimations() {
        if (MorphManager.#isUpdating) {
            return;
        }

        MorphManager.#isUpdating = true;
        try {
            for (const { asset } of this.#sourceStates.values()) {
                if (!asset || asset.destroyed) {
                    continue;
                }
                asset.clearMorphAnimations();
            }
            MorphManager.#clearPairMaps(this.#sourceStates);
            MorphManager.#clearTargetAssets(this.#targetAssets);
        } finally {
            MorphManager.#isUpdating = false;
        }
    }

    static #clearPairMaps(sourceStates: Map<string, MorphSourceState>) {
        for (const sourceId of sourceStates.keys()) {
            const targetId = MorphManager.#morphPairs.get(sourceId);
            if (targetId) {
                MorphManager.#reverseMorphPairs.delete(targetId);
            }
            MorphManager.#morphPairs.delete(sourceId);
        }
        sourceStates.clear();
    }

    static #clearTargetAssets(targetAssets: Set<Asset>) {
        for (const targetAsset of targetAssets) {
            MorphManager.#mediaTimeOffsets.delete(targetAsset.id);
            MorphManager.#allTargetAssets.delete(targetAsset.id);
            if (!targetAsset.destroyed) {
                MorphManager.#forceBuildAndDraw(targetAsset);
            }
        }
        targetAssets.clear();
    }

    #setupMorphForAssetPair(sourceAsset: Asset, targetAsset: Asset) {
        const { duration: morphDuration, interpolation } = sourceAsset._morphParameters;

        if (this.#slide.duration < morphDuration) {
            return;
        }

        if (!this.#sourceStates.has(sourceAsset.id)) {
            this.#sourceStates.set(sourceAsset.id, {
                asset: sourceAsset,
                x: sourceAsset.transformation.position.x,
                y: sourceAsset.transformation.position.y,
                scaleX: sourceAsset.scale.x,
                scaleY: sourceAsset.scale.y,
                alpha: sourceAsset.alpha,
                rotation: sourceAsset.transformation.rotation,
            });
        }

        this.#targetAssets.add(targetAsset);
        MorphManager.#allTargetAssets.set(targetAsset.id, targetAsset);
        MorphManager.#morphPairs.set(sourceAsset.id, targetAsset.id);
        MorphManager.#reverseMorphPairs.set(targetAsset.id, sourceAsset.id);

        if (
            Config.isMediaAsset(targetAsset) ||
            Config.isIconAsset(targetAsset) ||
            Config.isLottieAsset(targetAsset)
        ) {
            MorphManager.#mediaTimeOffsets.set(targetAsset.id, this.#slide.duration);
        }

        MorphManager.#forceBuildAndDraw(sourceAsset);
        MorphManager.#forceBuildAndDraw(targetAsset);

        const alphaOnly = sourceAsset.morphAlphaOnly;
        const animationConfigs = (() => {
            const assetConfigs = MorphManager.#buildAssetAnimationConfigs(sourceAsset, targetAsset);

            const hasMaskMorph =
                !alphaOnly && MorphManager.#setupMaskMorphState(sourceAsset, targetAsset);
            if (!hasMaskMorph) {
                return assetConfigs;
            }

            const maskConfigs = MorphManager.#buildMaskAnimationConfigs(sourceAsset, targetAsset);
            return { ...assetConfigs, ...maskConfigs };
        })();
        const excludedProperties = sourceAsset.morphExcludedProperties;
        Object.entries(animationConfigs).forEach(([morphableProperty, config]) => {
            if (alphaOnly && morphableProperty !== 'alpha') {
                return;
            }
            if (!alphaOnly && excludedProperties.includes(morphableProperty as MorphableProperty)) {
                return;
            }

            const sourceAnim = sourceAsset.addMorphAnimation();
            sourceAnim.property = config.property;
            sourceAnim.startValue = config.startValue;
            sourceAnim.endValue = config.endValue;
            sourceAnim.duration = morphDuration;
            sourceAnim.interpolation =
                config.property === 'alpha' && sourceAsset.morphAlphaStep
                    ? Config.STEP_INTERPOLATION_PRESET
                    : interpolation;
            sourceAnim.shouldOffsetPropertyValue = config.shouldOffsetPropertyValue;
        });
    }

    static #getXPosition(asset: Asset) {
        if (Config.isMediaAsset(asset) && asset.mask) {
            return asset.transform.position.x + asset.xTranslation;
        }
        return asset.x;
    }

    static #getYPosition(asset: Asset) {
        if (Config.isMediaAsset(asset) && asset.mask) {
            return asset.transform.position.y + asset.yTranslation;
        }
        return asset.y;
    }

    static #buildAssetAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        if (sourceAsset.alpha !== targetAsset.alpha) {
            configs['alpha'] = {
                property: 'alpha',
                shouldOffsetPropertyValue: false,
                startValue: sourceAsset.alpha,
                endValue: targetAsset.alpha,
            };
        }

        if (sourceAsset.transformation.rotation !== targetAsset.transformation.rotation) {
            configs['rotation'] = {
                property: 'rotation',
                shouldOffsetPropertyValue: false,
                startValue: sourceAsset.transformation.rotation,
                endValue: targetAsset.transformation.rotation,
            };
        }

        // Asset with Mask uses other properties for scale and position morph
        if (sourceAsset.mask) {
            return configs;
        }

        const sourceX = MorphManager.#getXPosition(sourceAsset);
        const targetX = MorphManager.#getXPosition(targetAsset);
        if (sourceX !== targetX) {
            configs['x'] = {
                property: 'x',
                shouldOffsetPropertyValue: false,
                startValue: sourceX,
                endValue: targetX,
            };
        }

        const sourceY = MorphManager.#getYPosition(sourceAsset);
        const targetY = MorphManager.#getYPosition(targetAsset);
        if (sourceY !== targetY) {
            configs['y'] = {
                property: 'y',
                shouldOffsetPropertyValue: false,
                startValue: sourceY,
                endValue: targetY,
            };
        }

        const scaleRatioX = sourceAsset.width > 0 ? targetAsset.width / sourceAsset.width : 1;
        if (scaleRatioX !== 1) {
            configs['scale.x'] = {
                property: 'scale.x',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioX,
            };
        }

        const scaleRatioY = sourceAsset.height > 0 ? targetAsset.height / sourceAsset.height : 1;
        if (scaleRatioY !== 1) {
            configs['scale.y'] = {
                property: 'scale.y',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioY,
            };
        }

        return configs;
    }

    static #buildMaskAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        configs['mask.transition'] = {
            property: 'mask.transition',
            shouldOffsetPropertyValue: false,
            startValue: 0,
            endValue: 1,
        };

        const sourceX = MorphManager.#getXPosition(sourceAsset);
        const targetX = MorphManager.#getXPosition(targetAsset);
        if (sourceX !== targetX) {
            configs['x'] = {
                property: 'x',
                shouldOffsetPropertyValue: false,
                startValue: sourceX,
                endValue: targetX,
            };
        }

        const sourceY = MorphManager.#getYPosition(sourceAsset);
        const targetY = MorphManager.#getYPosition(targetAsset);
        if (sourceY !== targetY) {
            configs['y'] = {
                property: 'y',
                shouldOffsetPropertyValue: false,
                startValue: sourceY,
                endValue: targetY,
            };
        }

        const sourceTransformX = sourceAsset.transform.position.x;
        const targetTransformX = targetAsset.transform.position.x;
        if (sourceTransformX !== targetTransformX) {
            configs['transform.position.x'] = {
                property: 'transform.position.x',
                shouldOffsetPropertyValue: false,
                startValue: sourceTransformX,
                endValue: targetTransformX,
            };
        }

        const sourceTransformY = sourceAsset.transform.position.y;
        const targetTransformY = targetAsset.transform.position.y;
        if (sourceTransformY !== targetTransformY) {
            configs['transform.position.y'] = {
                property: 'transform.position.y',
                shouldOffsetPropertyValue: false,
                startValue: sourceTransformY,
                endValue: targetTransformY,
            };
        }

        const bothAreMediaAssets =
            Config.isMediaAsset(sourceAsset) && Config.isMediaAsset(targetAsset);
        if (bothAreMediaAssets) {
            const maskScaleRatio = (() => {
                if (
                    !sourceAsset.mask ||
                    !targetAsset.mask ||
                    !Config.isStencilMask(sourceAsset.mask) ||
                    !Config.isStencilMask(targetAsset.mask)
                ) {
                    return 1;
                }
                const sourceMaskBounds = sourceAsset.mask.getMaxBounds();
                const targetMaskBounds = targetAsset.mask.getMaxBounds();
                const maskWidthRatio = targetMaskBounds.width / sourceMaskBounds.width;
                const maskHeightRatio = targetMaskBounds.height / sourceMaskBounds.height;
                return Math.min(maskWidthRatio, maskHeightRatio);
            })();

            const sourceScaleX = sourceAsset.scaleX;
            const targetScaleX = targetAsset.scaleX * maskScaleRatio;
            if (sourceScaleX !== targetScaleX) {
                configs['scaleX'] = {
                    property: 'scaleX',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceScaleX,
                    endValue: targetScaleX,
                };
            }

            const sourceScaleY = sourceAsset.scaleY;
            const targetScaleY = targetAsset.scaleY * maskScaleRatio;
            if (sourceScaleY !== targetScaleY) {
                configs['scaleY'] = {
                    property: 'scaleY',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceScaleY,
                    endValue: targetScaleY,
                };
            }

            const sourceXTranslation = sourceAsset.xTranslation;
            const targetXTranslation = targetAsset.xTranslation;
            if (sourceXTranslation !== targetXTranslation) {
                configs['xTranslation'] = {
                    property: 'xTranslation',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceXTranslation,
                    endValue: targetXTranslation,
                };
            }

            const sourceYTranslation = sourceAsset.yTranslation;
            const targetYTranslation = targetAsset.yTranslation;
            if (sourceYTranslation !== targetYTranslation) {
                configs['yTranslation'] = {
                    property: 'yTranslation',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceYTranslation,
                    endValue: targetYTranslation,
                };
            }
        }

        const bothAreRectangleAssets =
            Config.isRectangleAsset(sourceAsset) && Config.isRectangleAsset(targetAsset);
        if (bothAreRectangleAssets) {
            if (sourceAsset.rectWidth !== targetAsset.rectWidth) {
                configs['rectWidth'] = {
                    property: 'rectWidth',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceAsset.rectWidth,
                    endValue: targetAsset.rectWidth,
                };
            }
            if (sourceAsset.rectHeight !== targetAsset.rectHeight) {
                configs['rectHeight'] = {
                    property: 'rectHeight',
                    shouldOffsetPropertyValue: false,
                    startValue: sourceAsset.rectHeight,
                    endValue: targetAsset.rectHeight,
                };
            }
        }

        return configs;
    }

    static #setupMaskMorphState(sourceAsset: Asset, targetAsset: Asset): boolean {
        const sourceMask = sourceAsset.mask as StencilMask;
        const targetMask = targetAsset.mask as StencilMask;

        if (!sourceMask || !targetMask) {
            return false;
        }

        if (Config.isCircleMask(sourceMask) && Config.isCircleMask(targetMask)) {
            const sourceCircle = sourceMask as CircleMask;
            const targetCircle = targetMask as CircleMask;

            sourceCircle._setMorphState({
                startRadius: sourceCircle.endRadius,
                endRadius: targetCircle.endRadius,
                startCenter: { x: sourceCircle.centerX, y: sourceCircle.centerY },
                endCenter: { x: targetCircle.centerX, y: targetCircle.centerY },
            });
            sourceCircle.transition = 0;
            return true;
        }

        if (Config.isPolygonMask(sourceMask) && Config.isPolygonMask(targetMask)) {
            const sourcePolygon = sourceMask;
            const targetPolygon = targetMask;

            if (sourcePolygon.endPoints.length !== targetPolygon.endPoints.length) {
                return false;
            }

            const orderedSourceEndPoints = MorphManager.#orderPolygonPointsByCentroid(
                sourcePolygon.endPoints,
            );
            const orderedTargetEndPoints = MorphManager.#orderPolygonPointsByCentroid(
                targetPolygon.endPoints,
            );

            if (orderedSourceEndPoints.some(isNaN) || orderedTargetEndPoints.some(isNaN)) {
                return false;
            }

            sourcePolygon._setMorphState({
                startPoints: orderedSourceEndPoints,
                endPoints: orderedTargetEndPoints,
            });
            sourcePolygon.transition = 0;
            return true;
        }

        return false;
    }

    static #orderPolygonPointsByCentroid(points: number[]): number[] {
        const numPoints = points.length / 2;

        let centroidX = 0;
        let centroidY = 0;
        for (let i = 0; i < numPoints; i++) {
            centroidX += points[i * 2];
            centroidY += points[i * 2 + 1];
        }
        centroidX /= numPoints;
        centroidY /= numPoints;

        const pointsArray = Array.from({ length: numPoints }, (_, i) => {
            const x = points[i * 2];
            const y = points[i * 2 + 1];
            const angle = Math.atan2(y - centroidY, x - centroidX);
            return { x, y, angle };
        });

        pointsArray.sort((a, b) => a.angle - b.angle);

        return pointsArray.flatMap((point) => [point.x, point.y]);
    }

    static areLinkedPropertiesEqual<T>(a: T, b: T, specs: MorphLinkedPropertySpecs<T>): boolean {
        type Overrides = MorphLinkedPropertySpecs<T>[keyof T & string];
        return Object.entries(specs).every(([name, rawOverrides]) => {
            const prop = name as keyof T & string;
            const overrides = rawOverrides as Overrides;
            const isEqual = overrides?.isEqual ?? ((x: T, y: T) => x[prop] === y[prop]);
            return isEqual(a, b);
        });
    }

    static syncLinkedProperties<T>(
        target: T,
        source: T,
        specs: MorphLinkedPropertySpecs<T>,
    ): boolean {
        type Overrides = MorphLinkedPropertySpecs<T>[keyof T & string];
        let changed = false;
        for (const [name, rawOverrides] of Object.entries(specs)) {
            const prop = name as keyof T & string;
            const overrides = rawOverrides as Overrides;
            const isEqual = overrides?.isEqual ?? ((x: T, y: T) => x[prop] === y[prop]);
            if (isEqual(target, source)) continue;
            const sync =
                overrides?.sync ??
                ((t: T, s: T) => ((t as Record<string, unknown>)[prop] = s[prop]));
            sync(target, source);
            changed = true;
        }
        return changed;
    }

    static updateMorphAnimations(scenes: Scene[]) {
        const hasActiveTextEditing = scenes.some((scene) =>
            scene.getAssetsByType(Config.TEXT_ASSET_TYPE).some((a) => a.hasTextEditingEnabled),
        );
        if (hasActiveTextEditing) return;

        scenes.forEach((scene) => {
            scene._resetMorphAnimations();
        });

        scenes.forEach((scene) => {
            if (scene.morphEnabled) {
                scene._setupMorphAnimations();
            }
        });
    }

    static updateAdjacentMorphAnimations(modifiedScene: Scene) {
        const hasActiveTextEditing = modifiedScene.root.scenes.some((scene) =>
            scene.getAssetsByType(Config.TEXT_ASSET_TYPE).some((a) => a.hasTextEditingEnabled),
        );
        if (hasActiveTextEditing) return;

        const prevScene = modifiedScene.prevScene as Scene | undefined;

        if (prevScene?.morphEnabled) {
            prevScene._resetMorphAnimations();
            prevScene._setupMorphAnimations();
        }
        if (modifiedScene.morphEnabled) {
            modifiedScene._resetMorphAnimations();
            modifiedScene._setupMorphAnimations();
        }
    }

    static propagateLinkedProperties(modifiedScene: Scene) {
        if (MorphManager.#isUpdating) {
            return;
        }
        MorphManager.#isUpdating = true;
        try {
            let current = modifiedScene;
            while (current.morphEnabled && current.nextScene) {
                MorphManager.#propagateBetweenScenes(current, current.nextScene);
                current = current.nextScene;
            }

            current = modifiedScene;
            while (current.prevScene?.morphEnabled) {
                MorphManager.#propagateBetweenScenes(current, current.prevScene);
                current = current.prevScene;
            }
        } finally {
            MorphManager.#isUpdating = false;
        }
    }

    static #propagateBetweenScenes(fromScene: Scene, toScene: Scene) {
        const fromAssets = fromScene.getAssets({ deep: true });
        const toAssetMap = new Map(toScene.getAssets({ deep: true }).map((a) => [a.id, a]));

        for (const fromAsset of fromAssets) {
            const targetId = MorphManager.#morphPairs.get(fromAsset.id);
            if (targetId) {
                const target = toAssetMap.get(targetId);
                if (target?.syncMorphLinkedProperties(fromAsset)) {
                    target.requiresRedraw = true;
                }
            }

            const sourceId = MorphManager.#reverseMorphPairs.get(fromAsset.id);
            if (sourceId) {
                const source = toAssetMap.get(sourceId);
                if (source?.syncMorphLinkedProperties(fromAsset)) {
                    source.requiresRedraw = true;
                }
            }
        }
    }

    static #forceBuildAndDraw(asset: Asset) {
        const prevAutoDraw = asset.autoDraw;
        asset.autoDraw = true;
        asset.requiresRedraw = true;
        asset._buildAndDrawRecur();
        asset.autoDraw = prevAutoDraw;
    }

    static modifyNonMorphCurve(
        config: AnimationConfig,
        hasMorphLeadInAnimation: boolean,
        hasMorphAnimation: boolean,
    ) {
        if (hasMorphLeadInAnimation && hasMorphAnimation) {
            holdConfigCurveAtRest(config);
            return;
        }
        if (hasMorphAnimation) {
            const wouldRemoveBothEnds =
                config.removeAnchoredPoints === Config.START_ANIMATION_CURVE_ANCHOR;
            if (wouldRemoveBothEnds) {
                holdConfigCurveAtRest(config);
                return;
            }
            config.removeAnchoredPoints = Config.END_ANIMATION_CURVE_ANCHOR;
            return;
        }
        if (hasMorphLeadInAnimation) {
            const wouldRemoveBothEnds =
                config.removeAnchoredPoints === Config.END_ANIMATION_CURVE_ANCHOR;
            if (wouldRemoveBothEnds) {
                holdConfigCurveAtRest(config);
                return;
            }
            config.removeAnchoredPoints = Config.START_ANIMATION_CURVE_ANCHOR;
        }
    }
}
